'use client';

import { useState, useRef, useCallback } from 'react';
import type { FormType } from '@ra-mr/types';
import { submitFormSubmission } from '@/lib/api/public/form-submissions';

interface UseFormSubmissionOptions {
  formType: FormType;
  emailField: string;
  onSuccess?: () => void;
}

export function useFormSubmission({ formType, emailField, onSuccess }: UseFormSubmissionOptions) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const formLoadedAt = useRef(Date.now());

  const handleSubmit = useCallback(
    async (formData: Record<string, unknown>) => {
      setIsSubmitting(true);
      setSubmitStatus('idle');
      setErrorMessage('');

      const honeypotValue = formData['_honeypot'] as string | undefined;
      const email = formData[emailField] as string;

      // Remove internal fields from data
      const { _honeypot, ...data } = formData;

      try {
        await submitFormSubmission({
          formType,
          data,
          email,
          _honeypot: honeypotValue,
          _formLoadedAt: formLoadedAt.current,
        });
        setSubmitStatus('success');
        onSuccess?.();
      } catch {
        setSubmitStatus('error');
        setErrorMessage('There was an error submitting the form. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [formType, emailField, onSuccess]
  );

  return {
    isSubmitting,
    submitStatus,
    errorMessage,
    handleSubmit,
  };
}
